import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IMedData, IMedList, IUserBase } from '../../types/type';
import type { RootState } from '../store';
import { instance } from '../../api/api';

interface meddataState {
  data: IMedData | null;
  users: IUserBase[] | null;
  medicLists: any[] | null;
  totalCount: number;
  totalPages: number;
  error: string | null;
  loading: boolean;
}

const initialState: meddataState = {
  data: null,
  users: [],
  medicLists: null,
  totalCount: 0,
  totalPages: 0,
  error: null,
  loading: false,
};

export const fetchUsers = createAsyncThunk<IUserBase[], string>(
  'meddata/fetchUsers',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await instance.get<IUserBase[]>(`/medlist/all/${id}`);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message ?? error.message ?? 'Unknown error');
    }
  },
);

export const fetchMedlists = createAsyncThunk<IMedData, { id: string | number }>(
  'meddata/fetchMedlists',
  async ({ id }, { rejectWithValue }) => {
    try {
      const { data } = await instance.get<IMedData>(`/medlist/${id}`);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message ?? error.message ?? 'Unknown error');
    }
  },
);

export const updateUserMedData = createAsyncThunk<
  IMedData,
  { id: string | number; updates: Partial<IMedList> },
  { rejectValue: { message: string } }
>('meddata/updateUserMedData', async ({ id, updates }, { getState, rejectWithValue }) => {
  const profileID = (getState() as RootState).user.user?.id;
  try {
    const { data } = await instance.put<IMedData>(`/medlist/${id}`, { ...updates, medic: profileID });
    return data;
  } catch (error: any) {
    return rejectWithValue({ message: error.response?.data?.message ?? error.message ?? 'Unknown error' });
  }
});

const meddataSlice = createSlice({
  name: 'meddata',
  initialState,
  reducers: {
    setMed(state, action: PayloadAction<any>) {
      state.data = action.payload;
    },
    setMedicLists(state, action: PayloadAction<{ data: any[]; totalCount: number; totalPages: number }>) {
      state.medicLists = action.payload.data;
      state.totalCount = action.payload.totalCount;
      state.totalPages = action.payload.totalPages;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.users = action.payload;
        state.loading = false;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.error = action.error.message || 'Ошибка при загрузке пользователей';
        state.loading = false;
      })
      .addCase(fetchMedlists.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMedlists.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(fetchMedlists.rejected, (state, action) => {
        state.error = action.error.message || 'Ошибка при загрузке МедЛистов';
        state.loading = false;
      })
      .addCase(updateUserMedData.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUserMedData.fulfilled, (state, action) => {
        if (state.data) {
          state.data.medlist = { ...state.data?.medlist, ...action.payload.medlist };
        }
        state.loading = false;
      })
      .addCase(updateUserMedData.rejected, (state, action) => {
        state.error = action.payload?.message || 'Ошибка при обновлении МедЛиста';
        state.loading = false;
      });
  },
});

export const { setMed, setMedicLists } = meddataSlice.actions;
export default meddataSlice.reducer;
